import {
  Shield,
  Database,
  Eye,
  Lock,
  Trash2,
  AlertCircle,
  CheckCircle,
  XCircle,
  HardDrive,
  Laptop,
  Sparkles,
  Check,
  X,
  HelpCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useConsent } from '@/hooks/use-consent';
import { toast } from 'sonner';
import { STORAGE_KEYS, getKeysByType } from '@/lib/storage-keys';

export function PrivacyPolicy() {
  const { consentStatus, revokeConsent } = useConsent();

  const handleRevokeConsent = () => {
    revokeConsent();
    toast.success('All data cleared', {
      description: 'Your preferences have been deleted. The page will reload.',
    });
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <div className='space-y-8 max-w-6xl mx-auto'>
      {/* Header */}
      <div>
        <h1 className='text-3xl font-bold mb-3'>Privacy Policy</h1>
        <p className='text-muted-foreground'>Last updated: 24 September 2026</p>
      </div>

      {/* TL;DR Hero Card */}
      <Card className='p-8 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10'>
        <div className='flex items-start gap-4'>
          <div className='shrink-0 rounded-full bg-primary/10 p-3'>
            <Sparkles className='h-8 w-8 text-primary' />
          </div>
          <div>
            <h2 className='text-2xl font-bold mb-3'>The Simple Version</h2>
            <p className='text-lg leading-relaxed mb-4'>
              We don't collect, store, or track anything about you. Everything happens in your browser. No analytics, no cookies,
              no data sent to servers. You're in complete control.
            </p>
            <div className='flex flex-wrap gap-3'>
              <div className='flex items-center gap-2 text-sm'>
                <CheckCircle className='h-4 w-4 text-green-500' />
                <span>Private by design</span>
              </div>
              <div className='flex items-center gap-2 text-sm'>
                <CheckCircle className='h-4 w-4 text-green-500' />
                <span>No tracking</span>
              </div>
              <div className='flex items-center gap-2 text-sm'>
                <CheckCircle className='h-4 w-4 text-green-500' />
                <span>Your device only</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Current Status */}
      <Card className='p-6 border-2'>
        <div className='flex items-start gap-6'>
          <div className='shrink-0 rounded-full bg-primary/10 p-3'>
            <Shield className='h-7 w-7 text-primary' />
          </div>
          <div className='flex-1 space-y-4'>
            <div>
              <h3 className='font-bold text-lg mb-3'>Your Privacy Status</h3>

              {/* Status Badge */}
              <div className='flex items-center gap-3 mb-4'>
                <span className='text-sm text-muted-foreground'>Storage consent:</span>
                {consentStatus === 'accepted' && (
                  <Badge className='bg-green-500 hover:bg-green-600 text-white gap-1.5 text-sm py-1 px-3'>
                    <Check className='h-4 w-4' />
                    Accepted
                  </Badge>
                )}
                {consentStatus === 'declined' && (
                  <Badge
                    variant='destructive'
                    className='gap-1.5 text-sm py-1 px-3'
                  >
                    <X className='h-4 w-4' />
                    Declined
                  </Badge>
                )}
                {consentStatus === 'pending' && (
                  <Badge
                    variant='secondary'
                    className='gap-1.5 text-sm py-1 px-3'
                  >
                    <HelpCircle className='h-4 w-4' />
                    Not Set
                  </Badge>
                )}
              </div>

              {/* Status Description */}
              <div className='text-sm text-muted-foreground space-y-2'>
                {consentStatus === 'accepted' && <p>✓ Your preferences are being saved locally in your browser.</p>}
                {consentStatus === 'declined' && (
                  <p>✗ No data is being saved. This choice applies to this browser session only.</p>
                )}
                {consentStatus === 'pending' && (
                  <p>? You haven't made a choice yet. Accept the banner to enable preference storage.</p>
                )}
              </div>
            </div>

            {/* Action Button */}
            {consentStatus === 'accepted' && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant='destructive'
                    size='sm'
                    id='clear-data-button'
                  >
                    <Trash2 className='h-4 w-4 mr-2' />
                    Clear All Data & Revoke Consent
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear all data and revoke consent?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete all your saved preferences, favourites, API collections, and tool data. The
                      page will reload after clearing. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleRevokeConsent}
                      className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    >
                      Clear All Data
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </Card>

      {/* What We Don't Do - Visual Grid */}
      <section className='space-y-4'>
        <div className='flex items-center gap-3'>
          <Eye className='h-6 w-6 text-primary' />
          <h2 className='text-2xl font-bold'>What We DON'T Do</h2>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <Card className='p-5 border-destructive/20'>
            <div className='flex items-start gap-3'>
              <XCircle className='h-5 w-5 text-destructive shrink-0 mt-0.5' />
              <div>
                <p className='font-semibold mb-1'>No personal data</p>
                <p className='text-sm text-muted-foreground'>
                  We don't collect names, emails, IP addresses, or any identifying information.
                </p>
              </div>
            </div>
          </Card>

          <Card className='p-5 border-destructive/20'>
            <div className='flex items-start gap-3'>
              <XCircle className='h-5 w-5 text-destructive shrink-0 mt-0.5' />
              <div>
                <p className='font-semibold mb-1'>No tracking</p>
                <p className='text-sm text-muted-foreground'>
                  No Google Analytics, Facebook Pixel, or any tracking scripts whatsoever.
                </p>
              </div>
            </div>
          </Card>

          <Card className='p-5 border-destructive/20'>
            <div className='flex items-start gap-3'>
              <XCircle className='h-5 w-5 text-destructive shrink-0 mt-0.5' />
              <div>
                <p className='font-semibold mb-1'>No cookies</p>
                <p className='text-sm text-muted-foreground'>
                  Zero tracking cookies. We only use localStorage (with your permission).
                </p>
              </div>
            </div>
          </Card>

          <Card className='p-5 border-destructive/20'>
            <div className='flex items-start gap-3'>
              <XCircle className='h-5 w-5 text-destructive shrink-0 mt-0.5' />
              <div>
                <p className='font-semibold mb-1'>No data transmission</p>
                <p className='text-sm text-muted-foreground'>
                  Your data never leaves your browser (unless you explicitly use API tools).
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* How It Works - Visual */}
      <section className='space-y-4'>
        <div className='flex items-center gap-3'>
          <Laptop className='h-6 w-6 text-primary' />
          <h2 className='text-2xl font-bold'>How It Actually Works</h2>
        </div>

        <Card className='p-6 bg-primary/5'>
          <div className='space-y-4'>
            <div className='flex items-start gap-4'>
              <div className='shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary'>
                1
              </div>
              <div>
                <p className='font-semibold mb-1'>You visit the site</p>
                <p className='text-sm text-muted-foreground'>Our tools load into your browser. Nothing is sent to our servers.</p>
              </div>
            </div>

            <div className='flex items-start gap-4'>
              <div className='shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary'>
                2
              </div>
              <div>
                <p className='font-semibold mb-1'>You use a tool</p>
                <p className='text-sm text-muted-foreground'>
                  All processing happens in your browser's JavaScript engine. Your data stays on your device.
                </p>
              </div>
            </div>

            <div className='flex items-start gap-4'>
              <div className='shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary'>
                3
              </div>
              <div>
                <p className='font-semibold mb-1'>Optional: Save preferences</p>
                <p className='text-sm text-muted-foreground'>
                  If you accept, we save things like favourites and theme in localStorage (still only on your device).
                </p>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* What We Store Locally */}
      <section className='space-y-4'>
        <div className='flex items-center gap-3'>
          <HardDrive className='h-6 w-6 text-primary' />
          <h2 className='text-2xl font-bold'>What's Stored Locally (With Your Permission)</h2>
        </div>

        <p className='text-muted-foreground leading-relaxed'>
          If you accept storage consent, we save these preferences in your browser's storage. This data never leaves your device
          and can be deleted anytime.
        </p>

        {/* Storage Type Explanation */}
        <div className='grid sm:grid-cols-2 gap-4 mb-6'>
          <Card className='p-4 border-primary/20'>
            <div className='flex items-start gap-3'>
              <div className='shrink-0 rounded bg-primary/10 p-2'>
                <HardDrive className='h-5 w-5 text-primary' />
              </div>
              <div className='flex-1'>
                <h3 className='font-semibold text-sm mb-1'>Permanent Storage (localStorage)</h3>
                <p className='text-xs text-muted-foreground leading-relaxed'>
                  Data that persists even after closing your browser. Removed only when you clear browser data or revoke consent.
                </p>
              </div>
            </div>
          </Card>

          <Card className='p-4 border-blue-500/20'>
            <div className='flex items-start gap-3'>
              <div className='shrink-0 rounded bg-blue-500/10 p-2'>
                <Laptop className='h-5 w-5 text-blue-600' />
              </div>
              <div className='flex-1'>
                <h3 className='font-semibold text-sm mb-1'>Session Storage (sessionStorage)</h3>
                <p className='text-xs text-muted-foreground leading-relaxed'>
                  Temporary data for the current browser tab only. Automatically deleted when you close the tab.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Session Storage Section */}
        <h3 className='font-bold text-lg mt-8 mb-3'>Temporary Session Storage (sessionStorage)</h3>
        <p className='text-muted-foreground text-sm mb-4'>
          This storage type is temporary and exists only for your current browser tab. It automatically disappears when you close
          the tab or browser—no action needed.
        </p>

        <div className='grid gap-3'>
          {getKeysByType('sessionStorage').map((storageKey) => (
            <Card key={storageKey.key} className={storageKey.highlight ? 'p-4 border-blue-500/20 bg-blue-500/5' : 'p-4'}>
              <div className='flex items-start gap-3'>
                <div className={storageKey.highlight ? 'shrink-0 rounded bg-blue-500/10 p-2' : 'shrink-0 rounded bg-primary/10 p-2'}>
                  <Laptop className={storageKey.highlight ? 'h-4 w-4 text-blue-600' : 'h-4 w-4 text-primary'} />
                </div>
                <div className='flex-1'>
                  <div className='flex items-center justify-between mb-1'>
                    <p className='font-medium text-sm'>{storageKey.title}</p>
                    <code className='text-xs text-muted-foreground'>{storageKey.key}</code>
                  </div>
                  <p className='text-xs text-muted-foreground'>{storageKey.description}</p>
                  {storageKey.key === STORAGE_KEYS.CONSENT_SESSION.key && (
                    <div className='mt-2 p-2 bg-blue-500/10 rounded text-xs text-blue-700 dark:text-blue-400'>
                      <strong>Important:</strong> This setting disappears when you close the browser tab. Next time you visit, you'll
                      see the consent banner again. No permanent data is stored on your device when you decline.
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <h3 className='font-bold text-lg mt-6 mb-3'>Permanent Storage (localStorage)</h3>
        <div className='grid gap-3'>
          {getKeysByType('localStorage').map((storageKey) => {
            // Determine if this is a security-sensitive key (TOTP)
            const isSensitive = storageKey.key.includes('totp');
            const cardClass = isSensitive ? 'p-4 border-amber-500/20 bg-amber-500/5' : 'p-4';
            const iconBgClass = isSensitive ? 'shrink-0 rounded bg-amber-500/10 p-2' : 'shrink-0 rounded bg-primary/10 p-2';
            const IconComponent = isSensitive ? Lock : Database;
            const iconClass = isSensitive ? 'h-4 w-4 text-amber-600' : 'h-4 w-4 text-primary';

            return (
              <Card key={storageKey.key} className={cardClass}>
                <div className='flex items-start gap-3'>
                  <div className={iconBgClass}>
                    <IconComponent className={iconClass} />
                  </div>
                  <div className='flex-1'>
                    <div className='flex items-center justify-between mb-1'>
                      <p className='font-medium text-sm'>{storageKey.title}</p>
                      <code className='text-xs text-muted-foreground'>{storageKey.key}</code>
                    </div>
                    <p className='text-xs text-muted-foreground'>{storageKey.description}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <Card className='p-4 border-amber-500/20 bg-amber-500/5'>
          <div className='flex items-start gap-3'>
            <AlertCircle className='h-5 w-5 text-amber-500 shrink-0 mt-0.5' />
            <div className='space-y-2'>
              <p className='font-medium text-sm'>What's localStorage?</p>
              <p className='text-sm text-muted-foreground leading-relaxed'>
                It's a browser storage mechanism (like cookies, but more transparent). Unlike cookies, localStorage data is never
                automatically sent to servers. You can view it anytime in your browser's developer tools (Press{' '}
                <kbd className='px-2 py-1 bg-muted rounded text-xs'>F12</kbd> → Application → Local Storage).
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* Your Control */}
      <section className='space-y-4'>
        <div className='flex items-center gap-3'>
          <Lock className='h-6 w-6 text-primary' />
          <h2 className='text-2xl font-bold'>You're in Control</h2>
        </div>

        <div className='grid gap-4 md:grid-cols-2'>
          <Card className='p-5'>
            <h4 className='font-semibold mb-2 flex items-center gap-2'>
              <Eye className='h-4 w-4 text-primary' />
              View your data
            </h4>
            <p className='text-sm text-muted-foreground mb-3'>
              Press <kbd className='px-2 py-1 bg-muted rounded text-xs'>F12</kbd> in your browser, go to{' '}
              <strong>Application</strong> → <strong>Local Storage</strong> → select our domain. You'll see everything we've
              stored.
            </p>
          </Card>

          <Card className='p-5'>
            <h4 className='font-semibold mb-2 flex items-center gap-2'>
              <Trash2 className='h-4 w-4 text-primary' />
              Delete your data
            </h4>
            <p className='text-sm text-muted-foreground mb-3'>
              {consentStatus === 'accepted' ?
                <>
                  Use the{' '}
                  <a
                    href='#clear-data-button'
                    className='text-primary hover:underline'
                  >
                    "Clear All Data"
                  </a>{' '}
                  button above, or clear it via your browser settings, or delete individual items in developer tools. It's your
                  data, your choice.
                </>
              : <>
                  Clear data via your browser settings (Settings → Privacy → Clear browsing data), or delete individual items in
                  developer tools (F12 → Application → Local Storage).
                </>
              }
            </p>
          </Card>
        </div>
      </section>

      {/* Technical Details */}
      <section className='space-y-4'>
        <h2 className='text-2xl font-bold'>Technical Details</h2>

        <div className='space-y-3'>
          <details className='group'>
            <summary className='cursor-pointer font-semibold p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors'>
              Currency Converter - Automatic API requests
            </summary>
            <Card className='p-4 mt-2'>
              <p className='text-sm text-muted-foreground leading-relaxed mb-3'>
                The <strong>Currency Converter</strong> tool automatically fetches current exchange rates when you open it. This
                happens in your browser and connects to these free, public APIs:
              </p>
              <ul className='space-y-2 text-sm text-muted-foreground ml-4'>
                <li>
                  • <strong>Exchangerate.host</strong> - <code className='text-xs'>api.exchangerate.host</code> (default)
                </li>
                <li>
                  • <strong>Frankfurter (ECB)</strong> - <code className='text-xs'>api.frankfurter.app</code>
                </li>
                <li>
                  • <strong>NBP (Polish National Bank)</strong> - <code className='text-xs'>api.nbp.pl</code>
                </li>
              </ul>
              <p className='text-sm text-muted-foreground leading-relaxed mt-3'>
                <strong>What they might see:</strong> Your IP address and the currency pairs you're converting. These are free
                public services - check their privacy policies for details on data retention.
              </p>
            </Card>
          </details>

          <details className='group'>
            <summary className='cursor-pointer font-semibold p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors'>
              External API calls (when YOU initiate them)
            </summary>
            <Card className='p-4 mt-2'>
              <p className='text-sm text-muted-foreground leading-relaxed mb-3'>
                Some tools make network requests only when you explicitly use them:
              </p>
              <ul className='space-y-2 text-sm text-muted-foreground ml-4'>
                <li>
                  • <strong>API Request Builder:</strong> Sends requests to URLs you specify (your own APIs)
                </li>
                <li>
                  • <strong>Open Graph Preview:</strong> Fetches metadata from websites you enter via CORS proxies (
                  <code className='text-xs'>allorigins.win</code>, <code className='text-xs'>corsproxy.io</code>,{' '}
                  <code className='text-xs'>codetabs.com</code>)
                </li>
                <li>
                  • <strong>File to Base64:</strong> If you provide a URL, fetches that file to convert it
                </li>
              </ul>
              <p className='text-sm text-muted-foreground leading-relaxed mt-3'>
                These requests go directly from your browser to the target service. We don't intercept or log them. However, the
                target services may see your IP address.
              </p>
            </Card>
          </details>

          <details className='group'>
            <summary className='cursor-pointer font-semibold p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors'>
              External tool links
            </summary>
            <Card className='p-4 mt-2'>
              <p className='text-sm text-muted-foreground leading-relaxed'>
                We link to third-party developer tools (marked as "External"). When you click these links, you leave our site and
                are subject to their privacy policies. We're not responsible for their practices.
              </p>
            </Card>
          </details>

          <details className='group'>
            <summary className='cursor-pointer font-semibold p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors'>
              Hosting infrastructure
            </summary>
            <Card className='p-4 mt-2'>
              <p className='text-sm text-muted-foreground leading-relaxed mb-3'>
                This site is hosted on <strong>Hostinger</strong>. Standard web server logs (IP addresses, browser types, access
                times) may be collected by the hosting provider for technical operation and security. We don't use these logs for
                tracking or analytics.
              </p>
              <p className='text-sm text-muted-foreground'>
                See{' '}
                <a
                  href='https://www.hostinger.com/privacy-policy'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primary hover:underline'
                >
                  Hostinger's Privacy Policy
                </a>{' '}
                for details.
              </p>
            </Card>
          </details>
        </div>
      </section>

      {/* Legal Compliance */}
      <section className='space-y-4'>
        <h2 className='text-2xl font-bold'>Legal Compliance (GDPR, CCPA, etc.)</h2>

        <p className='text-muted-foreground leading-relaxed'>
          Since we don't collect personal data, most data protection regulations don't strictly apply. However, we follow best
          practices:
        </p>

        <div className='grid gap-3 md:grid-cols-2'>
          <Card className='p-4'>
            <CheckCircle className='h-5 w-5 text-green-500 mb-2' />
            <p className='font-medium text-sm mb-1'>Transparency</p>
            <p className='text-xs text-muted-foreground'>Clear information about what we do (and don't do)</p>
          </Card>

          <Card className='p-4'>
            <CheckCircle className='h-5 w-5 text-green-500 mb-2' />
            <p className='font-medium text-sm mb-1'>Consent</p>
            <p className='text-xs text-muted-foreground'>Explicit permission before storing anything</p>
          </Card>

          <Card className='p-4'>
            <CheckCircle className='h-5 w-5 text-green-500 mb-2' />
            <p className='font-medium text-sm mb-1'>Access</p>
            <p className='text-xs text-muted-foreground'>You can view all stored data anytime</p>
          </Card>

          <Card className='p-4'>
            <CheckCircle className='h-5 w-5 text-green-500 mb-2' />
            <p className='font-medium text-sm mb-1'>Deletion</p>
            <p className='text-xs text-muted-foreground'>Easy one-click data removal</p>
          </Card>
        </div>
      </section>

      {/* Updates */}
      <section className='space-y-4'>
        <h2 className='text-2xl font-bold'>Changes to This Policy</h2>
        <p className='text-muted-foreground leading-relaxed'>
          We may update this privacy policy occasionally. Changes will be posted here with an updated date. If we make material
          changes, we'll display a prominent notice on the site.
        </p>
      </section>

      {/* Questions */}
      <section className='space-y-4'>
        <h2 className='text-2xl font-bold'>Questions?</h2>
        <Card className='p-6 bg-muted/50'>
          <p className='text-muted-foreground leading-relaxed'>
            This site is maintained by <strong>Kocmouch</strong>. Since we don't collect any personal data, there isn't much to
            ask about – but if you have questions about how the tools work or this privacy policy, feel free to reach out via the
            website.
          </p>
        </Card>
      </section>

      {/* Bottom Summary */}
      <Card className='p-6 border-2 border-primary/20 bg-primary/5'>
        <h3 className='font-bold mb-3 text-lg'>Privacy Promise</h3>
        <div className='grid gap-2 text-sm'>
          <div className='flex items-center gap-2'>
            <CheckCircle className='h-4 w-4 text-green-500 shrink-0' />
            <span>Zero personal data collection</span>
          </div>
          <div className='flex items-center gap-2'>
            <CheckCircle className='h-4 w-4 text-green-500 shrink-0' />
            <span>No tracking, analytics, or cookies</span>
          </div>
          <div className='flex items-center gap-2'>
            <CheckCircle className='h-4 w-4 text-green-500 shrink-0' />
            <span>All tools run in your browser</span>
          </div>
          <div className='flex items-center gap-2'>
            <CheckCircle className='h-4 w-4 text-green-500 shrink-0' />
            <span>Optional localStorage for preferences only</span>
          </div>
          <div className='flex items-center gap-2'>
            <CheckCircle className='h-4 w-4 text-green-500 shrink-0' />
            <span>Complete user control</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
